import { WsClient, getWsUrl } from '../../client-lib/ws-client.js';
import { initAudio, playSound, loadSoundManifest, preloadCustomSound, isBuiltinChoice, BUILTIN_CHOICES } from '../../client-lib/sounds.js';
import type { SoundType } from '../../client-lib/sounds.js';
import type {
  ServerMessage, MsgStateSync, GamePhase, PlayMode, JudgeMode,
  PlayerInfo, BuzzRecord, QueuePosition, WrongAnswerMode, ReopenPolicy,
} from '../../shared/types.js';
import QRCode from 'qrcode';

// --- DOM Elements ---
const connectionStatus = document.getElementById('connection-status')!;
const roomIdDisplay = document.getElementById('room-id-display')!;
const playerTbody = document.getElementById('player-tbody')!;
const noPlayers = document.getElementById('no-players')!;
const phaseDisplay = document.getElementById('phase-display')!;
const buzzList = document.getElementById('buzz-list')!;
const currentAnswererEl = document.getElementById('current-answerer')!;
const judgeButtons = document.getElementById('judge-buttons')!;
const btnCorrect = document.getElementById('btn-correct')!;
const btnWrong = document.getElementById('btn-wrong')!;
const btnOpen = document.getElementById('btn-open') as HTMLButtonElement;
const btnReset = document.getElementById('btn-reset')!;
const settingsToggle = document.getElementById('settings-toggle')!;
const settingsBody = document.getElementById('settings-body')!;
const correctPointsInput = document.getElementById('correct-points') as HTMLInputElement;
const incorrectPointsInput = document.getElementById('incorrect-points') as HTMLInputElement;
const wrongAnswerModeSelect = document.getElementById('wrong-answer-mode') as HTMLSelectElement;
const playModeSelect = document.getElementById('play-mode') as HTMLSelectElement;
const judgeModeSelect = document.getElementById('judge-mode') as HTMLSelectElement;
const allowBuzzDuringAnswerSelect = document.getElementById('allow-buzz-during-answer') as HTMLSelectElement;
const reopenPolicyRow = document.getElementById('reopen-policy-row')!;
const reopenPolicySelect = document.getElementById('reopen-policy') as HTMLSelectElement;
const winCorrectCountInput = document.getElementById('win-correct-count') as HTMLInputElement;
const loseWrongCountInput = document.getElementById('lose-wrong-count') as HTMLInputElement;
const soundBuzzSelect = document.getElementById('sound-buzz') as HTMLSelectElement;
const soundCorrectSelect = document.getElementById('sound-correct') as HTMLSelectElement;
const soundIncorrectSelect = document.getElementById('sound-incorrect') as HTMLSelectElement;
const btnResetScores = document.getElementById('btn-reset-scores')!;
const btnNewRoom = document.getElementById('btn-new-room')!;
const btnQr = document.getElementById('btn-qr')!;
const qrModal = document.getElementById('qr-modal')!;
const qrUrl = document.getElementById('qr-url')!;
const qrCanvas = document.getElementById('qr-canvas') as HTMLCanvasElement;
const btnQrSave = document.getElementById('btn-qr-save')!;
const btnQrShare = document.getElementById('btn-qr-share')!;
const btnQrClose = document.getElementById('btn-qr-close')!;
const qrModalBackdrop = qrModal.querySelector('.qr-modal-backdrop')!;

// --- State ---
let currentPhase: GamePhase = 'idle';
let currentPlayMode: PlayMode = 'local';
let currentSoundBuzz = 'default';
let currentSoundCorrect = 'default';
let currentSoundIncorrect = 'default';
let currentAdsDisabled = false;

// Owner-only, undocumented: creating a room with a valid license token disables
// ads for that room. The token itself is validated server-side (src/server/license.ts).
// Not exposed anywhere in the UI — see FEZ-34/FEZ-41 for rationale and future plans.
const licenseToken = new URLSearchParams(window.location.search).get('license') ?? undefined;

// --- WebSocket ---
const ws = new WsClient(getWsUrl(), handleMessage, handleConnectionChange);

function handleConnectionChange(connected: boolean): void {
  connectionStatus.textContent = connected ? '接続中' : '切断中';
  connectionStatus.className = connected ? 'status-connected' : 'status-disconnected';

  if (connected) {
    const savedRoomId = localStorage.getItem('rjh_roomId') || undefined;
    const savedParentToken = localStorage.getItem('rjh_parentToken') || undefined;
    ws.send({
      type: 'register',
      role: 'parent',
      roomId: savedRoomId,
      parentToken: savedParentToken,
      licenseToken,
    });
  }
}

function handleMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case 'registered':
      localStorage.setItem('rjh_roomId', msg.roomId);
      if (msg.parentToken) {
        localStorage.setItem('rjh_parentToken', msg.parentToken);
      }
      roomIdDisplay.textContent = `Room: ${msg.roomId}`;
      break;

    case 'error':
      if (msg.code === 'PARENT_AUTH_FAILED') {
        connectionStatus.textContent = '親機認証エラー';
        connectionStatus.className = 'status-disconnected';
        ws.disconnect('parent authentication failed');
      }
      break;

    case 'state_sync':
      handleStateSync(msg);
      break;

    case 'buzzes_opened':
      // Handled via state_sync
      break;

    case 'buzz_result':
      renderBuzzes(msg.buzzes);
      break;

    case 'judge_result': {
      const label = msg.correct ? '正解' : '不正解';
      currentAnswererEl.textContent = `${msg.playerName}: ${label} (${msg.pointsDelta >= 0 ? '+' : ''}${msg.pointsDelta}点)`;
      if (currentPlayMode === 'local') {
        playSound(msg.correct ? 'correct' : 'incorrect', msg.correct ? currentSoundCorrect : currentSoundIncorrect);
      }
      break;
    }

    case 'answer_turn':
      currentAnswererEl.textContent = `解答権: ${getPlayerNameById(msg.playerId)}`;
      break;

    case 'round_reset':
      // Handled via state_sync
      break;
  }
}

let lastPlayers: PlayerInfo[] = [];

function getPlayerNameById(id: string): string {
  return lastPlayers.find(p => p.id === id)?.name ?? '???';
}

function handleStateSync(msg: MsgStateSync): void {
  currentPhase = msg.phase;
  lastPlayers = msg.players;

  // Update players table
  renderPlayers(msg.players);

  // Update phase display
  renderPhase(msg.phase);

  // Update buzzes
  renderBuzzes(msg.buzzes);

  // Update current answerer
  if (msg.currentAnswererId) {
    const name = getPlayerNameById(msg.currentAnswererId);
    currentAnswererEl.textContent = `解答権: ${name}`;
  } else {
    currentAnswererEl.textContent = '';
  }

  // Show/hide judge buttons
  judgeButtons.classList.toggle('hidden', msg.phase !== 'buzzed' || !msg.currentAnswererId);

  // Update button states
  const roundActive = msg.phase === 'accepting' || msg.phase === 'buzzed';
  btnOpen.disabled = roundActive;
  setSettingsDisabled(roundActive);

  // Update settings (always sync server state for sound playback)
  currentPlayMode = msg.settings.playMode;
  currentSoundBuzz = msg.settings.soundBuzz;
  currentSoundCorrect = msg.settings.soundCorrect;
  currentSoundIncorrect = msg.settings.soundIncorrect;
  currentAdsDisabled = msg.settings.adsDisabled;

  // Sync form values only when not in active round (avoid overwriting user edits)
  if (!roundActive) {
    correctPointsInput.value = String(msg.settings.correctPoints);
    incorrectPointsInput.value = String(msg.settings.incorrectPoints);
    wrongAnswerModeSelect.value = msg.settings.wrongAnswerMode;
    playModeSelect.value = msg.settings.playMode;
    winCorrectCountInput.value = String(msg.settings.winCorrectCount);
    loseWrongCountInput.value = String(msg.settings.loseWrongCount);
    reopenPolicySelect.value = msg.settings.reopenPolicy;
    judgeModeSelect.value = msg.settings.judgeMode;
    allowBuzzDuringAnswerSelect.value = String(msg.settings.allowBuzzDuringAnswer);
    soundBuzzSelect.value = msg.settings.soundBuzz;
    soundCorrectSelect.value = msg.settings.soundCorrect;
    soundIncorrectSelect.value = msg.settings.soundIncorrect;
    reopenPolicyRow.style.display = msg.settings.wrongAnswerMode === 'reopen' ? '' : 'none';
  }
}

function renderPlayers(players: PlayerInfo[]): void {
  if (players.length === 0) {
    noPlayers.style.display = 'block';
    playerTbody.innerHTML = '';
    return;
  }

  noPlayers.style.display = 'none';
  playerTbody.innerHTML = players.map((p, i) => {
    const statusClass = p.won ? 'player-won' : p.eliminated ? 'player-eliminated' : (p.connected ? 'player-online' : 'player-offline');
    const statusText = p.won ? '★ 勝抜' : p.eliminated ? '✕ 失格' : (p.connected ? '● オンライン' : '○ オフライン');
    return `
    <tr class="${p.won ? 'row-won' : p.eliminated ? 'row-eliminated' : ''}">
      <td>${i + 1}</td>
      <td>${escapeHtml(p.name)}</td>
      <td class="${statusClass}">${statusText}</td>
      <td>${p.score}</td>
      <td>${p.correctCount}</td>
      <td>${p.wrongCount}</td>
      <td class="score-adjust">
        <button class="btn-score-adj" data-pid="${p.id}" data-delta="-1">-1</button>
        <button class="btn-score-adj" data-pid="${p.id}" data-delta="1">+1</button>
      </td>
    </tr>
  `;
  }).join('');
}

function renderPhase(phase: GamePhase): void {
  phaseDisplay.className = phase;
  const labels: Record<GamePhase, string> = {
    idle: '待機中',
    accepting: '早押し受付中',
    buzzed: '解答中',
    judged: '判定済み',
  };
  phaseDisplay.textContent = labels[phase];
}

function renderBuzzes(buzzes: BuzzRecord[]): void {
  if (buzzes.length === 0) {
    buzzList.innerHTML = '<p style="color: var(--color-text-dim); text-align: center;">-</p>';
    return;
  }

  const base = buzzes[0].correctedTimestamp;
  buzzList.innerHTML = buzzes.map((b) => {
    const diff = b.correctedTimestamp - base;
    const diffStr = b.rank === 1 ? '' : `+${diff.toFixed(1)}ms`;
    return `
      <div class="buzz-entry ${b.rank === 1 ? 'first' : ''}">
        <span>${b.rank}位: ${escapeHtml(b.playerName)}</span>
        <span>${diffStr}</span>
      </div>
    `;
  }).join('');
}

// --- Event Handlers ---

function sendCurrentSettings(): void {
  ws.send({
    type: 'update_settings',
    correctPoints: parseInt(correctPointsInput.value, 10) || 10,
    incorrectPoints: parseInt(incorrectPointsInput.value, 10) || -5,
    wrongAnswerMode: wrongAnswerModeSelect.value as WrongAnswerMode,
    playMode: playModeSelect.value as PlayMode,
    winCorrectCount: parseInt(winCorrectCountInput.value, 10) || 0,
    loseWrongCount: parseInt(loseWrongCountInput.value, 10) || 0,
    reopenPolicy: reopenPolicySelect.value as ReopenPolicy,
    judgeMode: judgeModeSelect.value as JudgeMode,
    allowBuzzDuringAnswer: allowBuzzDuringAnswerSelect.value === 'true',
    soundBuzz: soundBuzzSelect.value,
    soundCorrect: soundCorrectSelect.value,
    soundIncorrect: soundIncorrectSelect.value,
    adsDisabled: currentAdsDisabled,
  });
}

function setSettingsDisabled(disabled: boolean): void {
  const inputs = settingsBody.querySelectorAll('input, select');
  inputs.forEach(el => (el as HTMLInputElement | HTMLSelectElement).disabled = disabled);
}

btnOpen.addEventListener('click', () => {
  initAudio();
  sendCurrentSettings();
  ws.send({ type: 'open_buzzes' });
});

btnReset.addEventListener('click', () => {
  ws.send({ type: 'reset' });
});

btnResetScores.addEventListener('click', () => {
  if (!confirm('全プレイヤーの得点・正誤数をリセットしますか？')) return;
  ws.send({ type: 'reset_scores' });
});

playerTbody.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest('.btn-score-adj') as HTMLElement | null;
  if (!btn) return;
  const playerId = btn.dataset.pid!;
  const delta = parseInt(btn.dataset.delta!, 10);
  ws.send({ type: 'adjust_score', playerId, delta });
});

btnCorrect.addEventListener('click', () => {
  ws.send({ type: 'judge', correct: true });
});

btnWrong.addEventListener('click', () => {
  ws.send({ type: 'judge', correct: false });
});

settingsToggle.addEventListener('click', () => {
  settingsBody.classList.toggle('hidden');
  settingsToggle.textContent = settingsBody.classList.contains('hidden') ? '設定 ▼' : '設定 ▲';
});

// Settings are auto-applied on "早押し開始" — no manual save needed

// Show/hide reopen policy based on wrong answer mode
wrongAnswerModeSelect.addEventListener('change', () => {
  reopenPolicyRow.style.display = wrongAnswerModeSelect.value === 'reopen' ? '' : 'none';
});

// Preload custom sound when selected
for (const select of [soundBuzzSelect, soundCorrectSelect, soundIncorrectSelect]) {
  select.addEventListener('change', () => {
    const choice = select.value;
    const type = select.id.replace('sound-', '') as SoundType;
    if (!isBuiltinChoice(choice)) {
      preloadCustomSound(choice, type);
    }
  });
}

// Preview sound buttons
document.querySelectorAll('.btn-preview[data-sound-type]').forEach(btn => {
  btn.addEventListener('click', () => {
    initAudio();
    const type = (btn as HTMLElement).dataset.soundType as SoundType;
    const selectMap: Record<string, HTMLSelectElement> = {
      buzz: soundBuzzSelect,
      correct: soundCorrectSelect,
      incorrect: soundIncorrectSelect,
    };
    playSound(type, selectMap[type].value);
  });
});

btnNewRoom.addEventListener('click', () => {
  if (!confirm('新しいルームを作成しますか？現在のルームは破棄されます。')) return;
  localStorage.removeItem('rjh_roomId');
  localStorage.removeItem('rjh_parentToken');
  location.reload();
});

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// --- Populate sound dropdowns with built-in + custom choices ---
function populateSoundSelects(manifestData: import('../../client-lib/sounds.js').SoundManifest): void {
  const selectMap: Record<SoundType, HTMLSelectElement> = {
    buzz: soundBuzzSelect,
    correct: soundCorrectSelect,
    incorrect: soundIncorrectSelect,
  };

  for (const type of ['buzz', 'correct', 'incorrect'] as SoundType[]) {
    const select = selectMap[type];
    select.innerHTML = '';

    // Built-in options
    for (const b of BUILTIN_CHOICES) {
      const opt = document.createElement('option');
      opt.value = b.id;
      opt.textContent = b.name;
      select.appendChild(opt);
    }

    // Custom options from manifest
    for (const entry of manifestData[type]) {
      const opt = document.createElement('option');
      opt.value = entry.id;
      opt.textContent = entry.name;
      select.appendChild(opt);
    }
  }

  // Re-apply current values
  soundBuzzSelect.value = currentSoundBuzz;
  soundCorrectSelect.value = currentSoundCorrect;
  soundIncorrectSelect.value = currentSoundIncorrect;
}

loadSoundManifest().then(data => {
  populateSoundSelects(data);
  // Preload current custom sounds
  for (const [type, choice] of [['buzz', currentSoundBuzz], ['correct', currentSoundCorrect], ['incorrect', currentSoundIncorrect]] as const) {
    if (!isBuiltinChoice(choice)) {
      preloadCustomSound(choice, type as SoundType);
    }
  }
});

// --- QR Code ---

function getChildUrl(): string {
  const roomId = localStorage.getItem('rjh_roomId') || '';
  const origin = window.location.origin;
  return `${origin}/child/?room=${roomId}`;
}

async function showQrModal(): Promise<void> {
  const url = getChildUrl();
  qrUrl.textContent = url;

  await QRCode.toCanvas(qrCanvas, url, {
    width: 280,
    margin: 1,
    color: { dark: '#000000', light: '#ffffff' },
  });

  // Hide share button if Web Share API is not available
  if (!navigator.share) {
    btnQrShare.style.display = 'none';
  } else {
    btnQrShare.style.display = '';
  }

  qrModal.classList.remove('hidden');
}

function hideQrModal(): void {
  qrModal.classList.add('hidden');
}

btnQr.addEventListener('click', showQrModal);
btnQrClose.addEventListener('click', hideQrModal);
qrModalBackdrop.addEventListener('click', hideQrModal);

btnQrSave.addEventListener('click', () => {
  const dataUrl = qrCanvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = dataUrl;
  const roomId = localStorage.getItem('rjh_roomId') || 'room';
  a.download = `rjh-room-${roomId}-qr.png`;
  a.click();
});

btnQrShare.addEventListener('click', async () => {
  try {
    const blob = await new Promise<Blob>((resolve) => {
      qrCanvas.toBlob((b) => resolve(b!), 'image/png');
    });
    const roomId = localStorage.getItem('rjh_roomId') || '';
    const file = new File([blob], `rjh-room-${roomId}-qr.png`, { type: 'image/png' });
    await navigator.share({
      title: `RJH 早押し - Room ${roomId}`,
      text: `このURLから参加してください: ${getChildUrl()}`,
      files: [file],
    });
  } catch (err) {
    // User cancelled or share failed — ignore
  }
});

// --- Connect ---
ws.connect();
